import ids from 'short-id';
import request from 'superagent';
import { isString } from 'underscore';

import {
  getEntireVideoById,
  getEntireVideoInstanceById,
} from 'redux/middleware/api/media-upload-service/actions';
import { cancelPolling } from 'redux/modules/common/actions';
import { entireVideoSelector } from 'redux/modules/entities/selectors';
import { ThunkAction } from 'redux/types';
import {
  VIDEO_UPLOAD_POLL_INTERVAL_MILLIS,
  VIDEO_UPLOAD_POLL_MAX_ATTEMPTS,
} from 'utils/constants';
import reduxPoll from 'utils/redux-poll';
import { camelToSnake } from 'utils/string';

import {
  ActionTypes,
  UploadEntireVideoOpts,
  UploadEntireVideoWaitFor,
} from './types';
import {
  createEntireVideoForUpload,
  findReusableEntireVideo,
  hashLocalFile,
  hydrateVideoUploadOnHit,
  readInstanceEntireVideoId,
  shouldKeepPollingEntireVideoSelector,
} from './utils';
import WaveformGenerationError from './WaveformGenerationError';

export function createActionTypes(camelName: string): ActionTypes {
  const lowercaseName = camelToSnake(camelName).toLowerCase();
  const uppercaseName = lowercaseName.replace(/-/g, '_').toUpperCase();

  const createActionType = (suffix: string) =>
    `app/${lowercaseName}/${uppercaseName}_${suffix}`;

  return {
    transferRequest: createActionType('ENTIRE_VIDEO_TRANSFER_REQUEST'),
    transferSuccess: createActionType('ENTIRE_VIDEO_TRANSFER_SUCCESS'),
    transferFailure: createActionType('ENTIRE_VIDEO_TRANSFER_FAILURE'),
    resolveAudioPeaksRequest: createActionType(
      'ENTIRE_VIDEO_RESOLVE_AUDIO_PEAKS_REQUEST',
    ),
    resolveAudioPeaksSuccess: createActionType(
      'ENTIRE_VIDEO_RESOLVE_AUDIO_PEAKS_SUCCESS',
    ),
    resolveAudioPeaksFailure: createActionType(
      'ENTIRE_VIDEO_RESOLVE_AUDIO_PEAKS_FAILURE',
    ),
    clear: createActionType('ENTIRE_VIDEO_CLEAR'),
  };
}

export function getActionTypes(actionTypesOrName: string | ActionTypes) {
  return isString(actionTypesOrName)
    ? createActionTypes(actionTypesOrName)
    : actionTypesOrName;
}

export default function createActions(actionTypesOrName: ActionTypes | string) {
  const actionTypes = getActionTypes(actionTypesOrName);
  const uploadPollId = ids.generate();

  const actions = {
    resolveAudioPeaks: (
      entireVideoId: number,
    ): ThunkAction<Promise<void>> => async (dispatch, getState) => {
      dispatch({
        type: actionTypes.resolveAudioPeaksRequest,
      });

      try {
        const entireVideo = entireVideoSelector(getState(), entireVideoId);
        const waveformUrl = entireVideo?.getIn(['waveform', 'url']);

        const { body } = await request.get(waveformUrl);

        dispatch({
          type: actionTypes.resolveAudioPeaksSuccess,
          payload: { audioPeaks: body?.data },
        });
      } catch (err) {
        dispatch({ type: actionTypes.resolveAudioPeaksFailure });

        // Surface a peaks-fetch failure as a waveform error, not the raw
        // network error. The video upload itself succeeded — only loading its
        // waveform peaks failed — and the consumer (useUploadVideoStep) shows
        // the user a message only for WaveformGenerationError, matching the
        // pollEntireVideoInstance path.
        throw new WaveformGenerationError();
      }
    },

    pollEntireVideoInstance: (
      entireVideoInstanceId: number,
      waitFor: UploadEntireVideoWaitFor,
    ): ThunkAction<Promise<number>> => async (dispatch, getState) => {
      await reduxPoll(
        dispatch,
        () => dispatch(getEntireVideoInstanceById({ entireVideoInstanceId })),
        {
          id: uploadPollId,
          intervalMillis: VIDEO_UPLOAD_POLL_INTERVAL_MILLIS,
          maxAttempts: VIDEO_UPLOAD_POLL_MAX_ATTEMPTS,
          shouldContinue: err => {
            if (err) {
              return false;
            }

            return shouldKeepPollingEntireVideoSelector(
              getState(),
              readInstanceEntireVideoId(getState(), entireVideoInstanceId),
              waitFor,
            );
          },
        },
      );

      const entireVideoId = readInstanceEntireVideoId(
        getState(),
        entireVideoInstanceId,
      );

      if (entireVideoId == null) {
        throw new WaveformGenerationError();
      }

      return entireVideoId;
    },

    pollEntireVideoById: (
      entireVideoId: number,
      waitFor: UploadEntireVideoWaitFor,
    ): ThunkAction<Promise<number>> => async (dispatch, getState) => {
      await reduxPoll(
        dispatch,
        () => dispatch(getEntireVideoById({ entireVideoId })),
        {
          id: uploadPollId,
          intervalMillis: VIDEO_UPLOAD_POLL_INTERVAL_MILLIS,
          maxAttempts: VIDEO_UPLOAD_POLL_MAX_ATTEMPTS,
          shouldContinue: err => {
            if (err) {
              return false;
            }

            return shouldKeepPollingEntireVideoSelector(
              getState(),
              entireVideoId,
              waitFor,
            );
          },
        },
      );

      return entireVideoId;
    },

    uploadEntireVideo: (
      opts?: UploadEntireVideoOpts,
      waitFor: UploadEntireVideoWaitFor = 'entireVideo',
    ): ThunkAction<Promise<number>> => async dispatch => {
      const { src, mediaImportedArgs, ...uploadOpts } = opts ?? {};

      dispatch({ type: actionTypes.transferRequest });

      try {
        const md5 = await hashLocalFile(src);

        const reusableEntireVideoId = md5
          ? await dispatch(findReusableEntireVideo(md5, waitFor))
          : undefined;

        let entireVideoId: number;
        let entireUploadedVideo;

        if (
          reusableEntireVideoId != null &&
          md5 &&
          src &&
          typeof src !== 'string'
        ) {
          entireUploadedVideo = await dispatch(
            hydrateVideoUploadOnHit(src, md5, uploadOpts),
          );
          entireVideoId = await dispatch(
            actions.pollEntireVideoById(reusableEntireVideoId, waitFor),
          );
        } else {
          const created = await dispatch(
            createEntireVideoForUpload({
              src,
              md5,
              uploadOpts,
              mediaImportedArgs,
            }),
          );
          entireUploadedVideo = created.entireUploadedVideo;
          entireVideoId = await dispatch(
            actions.pollEntireVideoInstance(
              created.entireVideoInstanceId,
              waitFor,
            ),
          );
        }

        await dispatch(actions.resolveAudioPeaks(entireVideoId));

        dispatch({
          type: actionTypes.transferSuccess,
          payload: { entireVideoId, entireUploadedVideo },
        });

        return entireVideoId;
      } catch (err) {
        dispatch({ type: actionTypes.transferFailure });

        throw err;
      }
    },

    cancelPolling: (): ThunkAction<void> => dispatch => {
      dispatch(cancelPolling(uploadPollId));
    },

    clearEntireVideo: () => ({ type: actionTypes.clear }),
  };

  return actions;
}
